<template>
  <v-container fluid class="payment-container">
    <stepper-progress :current-step="2"></stepper-progress>

    <!-- ===== MOBILE SUMMARY CARD (Visible on phones) ===== -->
    <v-card class="mobile-summary-card hidden-md-and-up mb-4">
      <v-card-title class="summary-title">
        <v-icon left color="primary" small>mdi-receipt</v-icon>
        Booking Summary
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="pa-3">
        <div class="summary-row">
          <span class="summary-label">Total Amount:</span>
          <span class="summary-value primary--text font-weight-bold">
            {{ singleBooking.currency || 'KES' }} 
            {{ Number(singleBooking.booking_payment_balance || 0).toFixed(2) }}
          </span>
        </div>
        <div class="summary-row" v-if="totalOnlineDonations > 0">
          <span class="summary-label">Donation:</span>
          <span class="summary-value">
            {{ browserCurrency || 'KES' }} 
            {{ Number(totalOnlineDonations || 0).toFixed(2) }}
          </span>
        </div>
        <div class="summary-row" v-if="totalOnlineDonations > 0">
          <span class="summary-label font-weight-bold">Total Payable:</span>
          <span class="summary-value primary--text font-weight-bold">
            {{ browserCurrency || 'KES' }} 
            {{ Number(totalPayableAmount || 0).toFixed(2) }}
          </span>
        </div>
        <div class="summary-row">
          <span class="summary-label">Status:</span>
          <span class="summary-value" :class="{'green--text': singleBooking.booking_payment_status === 'Paid', 'orange--text': singleBooking.booking_payment_status !== 'Paid'}">
            {{ singleBooking.booking_payment_status || 'Pending' }}
          </span>
        </div>
      </v-card-text>
    </v-card>

    <div class="payment-header">
      <h2 class="earth-color payment-title">
        BOOKING PAYMENT
      </h2>
      <div class="payment-progress">
        <div class="progress-step active">1. Review</div>
        <div class="progress-step active">2. Payment</div>
        <div class="progress-step">3. Confirmation</div>
      </div>
    </div>

    <v-container fluid class="payment-content">
      <v-layout row wrap>
        <v-flex md3 class="sidebar-col">
          <side-bar></side-bar>
        </v-flex>

        <v-flex md8 ml-5 class="main-content">
          <div class="donation-card">
            <h4 class="earth-color donation-title">
              <v-icon color="earth">mdi-heart</v-icon>
              DONATE TO CONSERVATION EFFORTS
            </h4>
            <v-layout row wrap class="donation-form">
              <v-flex lg4 md4 sm12 xs12>
                <v-container>
                  <v-autocomplete label="Currency"
                                  v-model="on_payment_donation.donation_currency"
                                  :items="allCurrencies"
                                  @change="changeCurrencyDonation"
                                  :return-object="true"
                                  item-text="currency_name"
                                  item-value="currency_public_id"
                                  prepend-icon="mdi-currency-sign"></v-autocomplete>
                </v-container>
              </v-flex>
              <v-flex lg4 md4 sm12 xs12>
                <v-container>
                  <v-text-field label="Amount"
                                v-model="on_payment_donation.donation_amount"
                                type="number"
                                prepend-icon="mdi-cash"></v-text-field>
                </v-container>
              </v-flex>
              <v-flex lg4 d4 sm12 xs12>
                <v-container>
                  <v-btn :depressed="true"
                         color="primary"
                         @click="addOnlinePaymentDonation"
                         class="donate-btn">
                    <v-icon left>mdi-plus</v-icon>
                    Add Donation
                  </v-btn>
                </v-container>
              </v-flex>
            </v-layout>
          </div>

          <div class="payment-methods">
            <h3 class="earth-color methods-title">SELECT PAYMENT METHOD</h3>
            <v-layout row wrap class="method-cards">
              <!-- Mpesa Card -->
              <v-flex lg4 md4 sm6 xs12 class="method-item" v-if="paymentKESPrice < 250000">
                <v-card class="method-card"
                        :class="{ 'active-method': payment_mode === 'Mpesa', 'selected-method': payment_mode === 'Mpesa' }"
                        @click="payment_mode = 'Mpesa'">
                  <div class="method-icon">
                    <img src="https://marketplace.datingpro.com/image/data/payment%20systems/m-pesa-integration.png"
                         alt="Mpesa" />
                  </div>
                  <v-card-title primary-title class="method-title">
                    <div>
                      <h3 class="earth-color method-name">Pay via Mpesa</h3>
                      <div class="method-description">
                        Instant mobile payments for Kenya
                      </div>
                    </div>
                  </v-card-title>
                  <v-card-actions class="method-selection">
                    <v-radio v-model="payment_mode"
                             v-if="paymentKESPrice < 250000"
                             value="Mpesa"
                             color="primary"
                             :input-value="payment_mode === 'Mpesa'"
                             dense></v-radio>
                    <div class="selected-indicator" v-if="payment_mode === 'Mpesa'">
                      <v-icon color="primary" small>mdi-check-circle</v-icon>
                      <span>Selected</span>
                    </div>
                  </v-card-actions>
                </v-card>
              </v-flex>

              <!-- Card Payment -->
              <v-flex lg4 md4 sm6 xs12 class="method-item">
                <v-card class="method-card"
                        :class="{ 'active-method': payment_mode === 'Card', 'selected-method': payment_mode === 'Card' }"
                        @click="payment_mode = 'Card'">
                  <div class="method-icon">
                    <img src="https://www.freeiconspng.com/uploads/credit-cards-icon-25.png"
                         alt="Credit Card" />
                  </div>
                  <v-card-title primary-title class="method-title">
                    <div>
                      <h3 class="earth-color method-name">Pay via Card</h3>
                      <div class="method-description">
                        Visa, Mastercard & other cards
                      </div>
                    </div>
                  </v-card-title>
                  <v-card-actions class="method-selection">
                    <v-radio v-model="payment_mode"
                             value="Card"
                             color="primary"
                             :input-value="payment_mode === 'Card'"
                             dense></v-radio>
                    <div class="selected-indicator" v-if="payment_mode === 'Card'">
                      <v-icon color="primary" small>mdi-check-circle</v-icon>
                      <span>Selected</span>
                    </div>
                  </v-card-actions>
                </v-card>
              </v-flex>
            </v-layout>
          </div>

          <!-- Card Payment Form -->
          <transition name="slide-fade">
            <div v-if="payment_mode === 'Card'" class="payment-form">
              <v-card class="form-card">
                <v-card-title class="form-title earth-color">
                  <v-icon left color="earth">mdi-credit-card</v-icon>
                  CARD PAYMENT DETAILS
                </v-card-title>
                <v-container>
                  <form id="myform"
                        name="myform"
                        method="POST"
                        action="https://portal.host.iveri.com/Lite/Authorise.aspx">
                    <!-- Hidden form fields -->
                    <input v-if="singleBooking.booking_currency_name === 'USD'"
                           id="Lite_Merchant_Applicationid"
                           name="Lite_Merchant_Applicationid"
                           type="hidden"
                           :value="live_id"
                           readonly="true" />
                    <input v-if="singleBooking.booking_currency_name === 'KES'"
                           id="Lite_Merchant_Applicationid"
                           name="Lite_Merchant_Applicationid"
                           type="hidden"
                           :value="kes_live_id"
                           readonly="true" />
                    <input v-if="singleBooking.booking_currency_name === 'USD'"
                           id="Lite_Order_Amount"
                           name="Lite_Order_Amount"
                           type="hidden"
                           :value="bookingPaymentUSDPrice"
                           readonly="true" />
                    <input v-if="singleBooking.booking_currency_name === 'KES'"
                           id="Lite_Order_Amount"
                           name="Lite_Order_Amount"
                           type="hidden"
                           :value="bookingPaymentUSDPrice"
                           readonly="true" />
                    <input type="hidden"
                           name="Lite_Website_Successful_url"
                           :value="iveri_catcher_url + 'booking-payment'" />
                    <input type="hidden"
                           name="Lite_Website_Fail_url"
                           :value="iveri_catcher_url + 'iveri-payment-failure'" />
                    <input type="hidden"
                           name="Lite_Website_TryLater_url"
                           :value="iveri_catcher_url + 'iveri-payment-failure'" />
                    <input type="hidden"
                           name="Lite_Website_Error_url"
                           :value="iveri_catcher_url + 'iveri-payment-failure'" />
                    <input id="Lite_Currency_AlphaCode"
                           name="Lite_Currency_AlphaCode"
                           type="hidden"
                           :value="singleBooking.booking_currency_name"
                           readonly="true" />
                    <input id="Lite_Order_LineItems_Product_1"
                           name="Lite_Order_LineItems_Product_1"
                           type="hidden"
                           value="Booking Total"
                           readonly="true" />
                    <input id="Lite_Order_LineItems_Quantity_1"
                           name="Lite_Order_LineItems_Quantity_1"
                           type="hidden"
                           value="1"
                           readonly="true" />
                    <input id="Lite_Order_LineItems_Amount_1"
                           name="Lite_Order_LineItems_Amount_1"
                           type="hidden"
                           :value="bookingPaymentUSDPrice"
                           readonly="true" />
                    <input id="Lite_ConsumerOrderID_PreFix"
                           name="Lite_ConsumerOrderID_PreFix"
                           type="hidden"
                           value="DML"
                           readonly="true" />
                    <input id="Ecom_BillTo_Online_Email"
                           name="Ecom_BillTo_Online_Email"
                           type="hidden"
                           :value="singleBooking.booking_details.email_address" />
                    <input id="Ecom_Payment_Card_Protocols"
                           name="Ecom_Payment_Card_Protocols"
                           type="hidden"
                           value="iVeri"
                           readonly="true" />
                    <input id="BOOKINGID"
                           name="BOOKINGID"
                           type="hidden"
                           :value="singleBooking.booking_public_id" />
                    <input id="PAYMENTREF" name="PAYMENTREF" type="hidden" :value="cardPaymentReference" />
                    <input id="Ecom_TransactionComplete"
                           name="Ecom_TransactionComplete"
                           type="hidden"
                           value
                           readonly="true" />

                    <v-text-field id="txtEmail"
                                  name="txtEmail"
                                  v-model="singleBooking.booking_details.email_address"
                                  :rules="requiredRules"
                                  label="Email"
                                  required
                                  prepend-icon="mdi-email"></v-text-field>

                    <input type="text"
                           id="Amount"
                           hidden="hidden"
                           name="Amount"
                           class="text"
                           :value="bookingPaymentUSDPrice" />

                    <div class="payment-actions">
                      <v-btn :depressed="true"
                             type="submit"
                             id="btnsubmit"
                             class="btn primary"
                             name="btnsubmit"
                             @click="checkForPaymentDonation()"
                             color="primary"
                             large
                             block>
                        <v-icon left>mdi-lock</v-icon>
                        PROCEED TO SECURE PAYMENT
                      </v-btn>
                    </div>

                    <div class="payment-security">
                      <v-icon color="green">mdi-shield-check</v-icon>
                      <span>Your payment is secured with 256-bit encryption</span>
                    </div>

                    <div class="email-confirmation earth-color">
                      <v-icon color="earth">mdi-alert-circle</v-icon>
                      PLEASE RE-CONFIRM YOUR EMAIL ADDRESS TO PROCEED WITH PAYMENT
                    </div>
                  </form>
                </v-container>
              </v-card>
            </div>
          </transition>

          <!-- Mpesa Payment Form -->
          <transition name="slide-fade">
            <div v-if="payment_mode === 'Mpesa'" class="payment-form">
              <v-card class="form-card">
                <v-card-title class="form-title earth-color">
                  <v-icon left color="earth">mdi-cellphone</v-icon>
                  MPESA PAYMENT
                </v-card-title>
                <v-container>
                  <div class="mpesa-notice earth-color">
                    <v-icon color="earth">mdi-information</v-icon>
                    KINDLY NOTE THAT PAYMENTS ABOVE KES 250,000 CANNOT BE PROCESSED
                  </div>

                  <v-text-field v-model="mpesa.phone_number"
                                :rules="requiredRules"
                                label="Payer's Safaricom number"
                                required
                                prepend-icon="mdi-phone"></v-text-field>

                  <div class="payment-actions">
                    <v-btn :depressed="true"
                           type="submit"
                           id="btnsubmit"
                           class="btn primary"
                           name="btnsubmit"
                           color="primary"
                           block
                           large
                           @click="requestPayment()">
                      <v-icon left>mdi-send</v-icon>
                      REQUEST PAYMENT
                    </v-btn>
                  </div>

                  <transition name="fade">
                    <div v-if="payment_requested === true" class="confirm-payment">
                      <v-btn :depressed="true"
                             color="warning"
                             block
                             large
                             @click="checkPayment()"
                             :loading="$loadingTrue">
                        <v-icon left>mdi-check-circle</v-icon>
                        CONFIRM PAYMENT
                      </v-btn>
                      <div class="payment-instructions">
                        Check your phone for M-Pesa prompt and enter your PIN to complete payment
                      </div>
                    </div>
                  </transition>
                </v-container>
              </v-card>
            </div>
          </transition>
        </v-flex>
      </v-layout>
    </v-container>
    {{ redirectIfPaid }}
    {{ currencyChangeBasedOnPaymentType }}
  </v-container>
</template>

<script>
  import FrontNavigation from "../Repeated/FrontNavigation";
  import SideBar from "../Repeated/SideBar";
  import myCaptcha from "vue-captcha";
  export default {
    components: {
      "my-captcha": myCaptcha,
      SideBar,
      FrontNavigation,
    },
    name: "payment-information",
    data() {
      return {
        on_payment_donation: {
          donation_currency_id: "",
          donation_currency_name: this.browserCurrency,
          donation_amount: "",
          donation_currency_buy_rate: "",
          donation_currency_sell_rate: "",
        },
        requiredRules: [(v) => !!v || "This field is required"],
        Lite_Order_LineItems_Product: "Lite_Order_LineItems_Product_",
        Lite_Order_LineItems_Quantity: "Lite_Order_LineItems_Quantity_",
        Lite_Order_LineItems_Amount: "Lite_Order_LineItems_Amount_",
        payment_requested: false,
        card_button: false,
        items: "",
        mpesa: {
          phone_number: "254",
        },
        payment_mode: "",
        iveri_url: "https://bookings.olpejetaconservancy.org/",
        iveri_catcher_url: "https://bookings.olpejetaconservancy.org:5019/",
      };
    },
    methods: {
      checkPayment() {
        this.$store.dispatch("loading_true");
        this.$store.dispatch("get_booking", this.$route.params.booking_id);
        this.payment_mode = "Mpesa";
      },
      captchaOk() {
        this.card_button = true;
      },
      requestPayment: function () {
        this.payment_requested = true;
        let localDonation = this.$store.state.DonationStore.online_donation;
        let donation_infor = [];
        if (localDonation.length > 0) {
          let total_donation = this.totalOnlineDonations;
          donation_infor = [
            {
              new_donation_currency_id: this.browserCurrencyID,
              new_donation_currency_name: this.browserCurrency,
              new_donation_amount: total_donation,
              new_donation_currency_buy_rate: 1,
              new_donation_currency_sell_rate: 1,
            },
          ];
        }
        var data = {
          phone: this.mpesa.phone_number,
          booking_id: this.$route.params.booking_id,
          public_donations: donation_infor,
          session_id: "guest",
        };
        this.$store.dispatch("booking_mpesa_request", data);
      },
      changeCurrencyDonation(name) {
        this.on_payment_donation.donation_currency_id = name.currency_public_id;
        this.on_payment_donation.donation_currency_name = name.currency_name;
        this.on_payment_donation.donation_currency_buy_rate =
          name.buying_selling[0].buy_amount;
        this.on_payment_donation.donation_currency_sell_rate =
          name.buying_selling[0].sell_amount;
      },
      addOnlinePaymentDonation() {
        this.totalOnlineDonations;
        let online_donation = {
          new_donation_currency_id: this.on_payment_donation.donation_currency_id,
          new_donation_currency_name:
            this.on_payment_donation.donation_currency_name,
          new_donation_amount: this.on_payment_donation.donation_amount,
          new_donation_currency_buy_rate:
            this.on_payment_donation.donation_currency_buy_rate,
          new_donation_currency_sell_rate:
            this.on_payment_donation.donation_currency_sell_rate,
        };
        this.$store.commit("ADD_ONLINE_DONATION", online_donation);
        this.paymentUSDPrice;
      },
      checkForPaymentDonation() {
        let localDonation = this.$store.state.DonationStore.online_donation;
        let donation_infor = [];
        if (localDonation.length > 0) {
          let total_donation = this.totalOnlineDonations;
          donation_infor = [
            {
              new_donation_currency_id: this.browserCurrencyID,
              new_donation_currency_name: this.browserCurrency,
              new_donation_amount: total_donation,
            },
          ];
        }

        let bookingId = this.singleBooking.booking_public_id;
        let bookingRefCode = this.singleBooking.booking_reference ||
                             this.singleBooking.booking_ref_code ||
                             this.singleBooking.reference ||
                             this.singleBooking.booking_code ||
                             this.singleBooking.code ||
                             'BK-' + bookingId.substring(0, 8);

        let cardRef = 'CARD-' + Date.now() + '-' + Math.random().toString(36).substr(2, 8);

        let payment_donations = {
          public_donations: donation_infor,
          booking_id: bookingId,
          booking_ref_code: bookingRefCode,
          payment_method: 'enjkr4k4',
          payment_status: 0,
          payment_reference: cardRef,
          session_id: localStorage.getItem("lkjhgfdsa") || 'guest',
          user_public_id: localStorage.getItem('user_public_id') || '',
          card_tx_code: cardRef
        };
        this.$store.dispatch("post_iveri_donation", payment_donations);
        this.cardPaymentReference = cardRef;
      },
    },

    computed: {
  // ===== FIXED: No optional chaining =====
  totalPayableAmount() {
    const balance = parseFloat(
      this.singleBooking && this.singleBooking.booking_payment_balance 
        ? this.singleBooking.booking_payment_balance 
        : 0
    ) || 0;
    const donation = parseFloat(this.totalOnlineDonations) || 0;
    return (balance + donation).toFixed(2);
  },

  currencyChangeBasedOnPaymentType() {
    if (this.payment_mode === "Mpesa") {
      var kes = {
        created_at: null,
        currency_description: "The Kenyan Shilling.",
        currency_name: "KES",
        currency_public_id: "162fface-f5f1-41de-913b-d2bb784dda3a",
        currency_sign: "KES",
        session_id: "Admin",
        updated_at: null,
      };
      this.currencyChange(kes);
    } else {
      var singleBooking = this.singleBooking;
      var currencyChange = this.currencyChange;
      this.allCurrencies.map(function (currency) {
        if (singleBooking.currency !== undefined) {
          if (currency.currency_name === singleBooking.currency) {
            currencyChange(currency);
          }
        }
      });
    }
    return null;
  },
  redirectIfPaid: function () {
    if (this.singleBooking.booking_payment_status === "Paid") {
      window.location.replace(
        "/success/booking/" + this.$route.params.booking_id
      );
    }
    return null;
  },
  paymentKESPrice: function () {
    if (this.singleBooking.currency !== undefined) {
      this.currencyChange(this.singleBooking.currency);
      var total = 0;
      total = this.convert2(
        this.singleBooking.currency,
        "KES",
        this.singleBooking.booking_payment_balance
      ).amount;
      return total;
    }
  },
  bookingPaymentUSDPrice: function () {
    if (this.singleBooking.currency !== undefined) {
      let donationAmout = this.totalOnlineDonations;
      if (this.singleBooking.currency === "KES") {
        let paying_amount = this.paymentKESPrice;
        let total_payment_kes = paying_amount + donationAmout;
        return total_payment_kes * 100;
      } else {
        let booking_total_iveri = Number(
          this.convert2(
            this.singleBooking.currency,
            "USD",
            this.singleBooking.booking_payment_balance
          ).amount
        );
        let total_payment_usd =
          booking_total_iveri + Math.ceil(donationAmout);
        return total_payment_usd * 100;
      }
    }
    return this.paymentUSDPrice;
  },
  itemsToSend: function () {
    var singleBooking = this.singleBooking;
    var items = this.items;
    var instance = this;
    if (singleBooking.guest_total > 0) {
      for (let i = 0; i < singleBooking.guest_total; i++) {
        if (singleBooking.guests[i].payment_guests > 0) {
          items += items + "," + singleBooking.guests[i].payment_person;
          instance.items = items;
        }
      }
    }
    return this.items;
  },
},
    beforeCreate() {
      this.$store.dispatch("get_payment_platforms");
      this.$store.dispatch("get_jenga_auth");
    },
    created() {
      this.$store.dispatch("get_booking", this.$route.params.booking_id);
    },
  };
</script>

<style scoped>
  @font-face {
    font-family: 'Earthwerk';
    src: url('PizzaDude.dk-Earthwerk.otf');
    font-weight: normal;
    font-style: normal;
  }

  /* ============================================
     MAIN CONTAINER
     ============================================ */
  .payment-container {
    background-color: #f9f9f9;
    min-height: 100vh;
    padding: 16px;
  }

  /* ============================================
     PAYMENT HEADER
     ============================================ */
  .payment-header {
    text-align: center;
    margin-bottom: 20px;
    padding: 16px 20px;
    background: linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%);
    border-radius: 10px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  }

  .payment-title {
    font-family: 'Earthwerk', sans-serif;
    font-size: 2rem;
    margin-bottom: 12px;
    letter-spacing: 1px;
    color: #4a6b22;
    text-shadow: 1px 1px 2px rgba(0,0,0,0.1);
  }

  .payment-progress {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 12px;
  }

  .progress-step {
    padding: 8px 16px;
    background-color: #e0e0e0;
    border-radius: 20px;
    font-weight: 500;
    color: #666;
    position: relative;
    font-size: 0.85rem;
  }

  .progress-step.active {
    background-color: #74a533;
    color: white;
    box-shadow: 0 4px 8px rgba(116, 165, 51, 0.3);
  }

  .progress-step:not(:last-child):after {
    content: '→';
    position: absolute;
    right: -15px;
    color: #999;
  }

  /* ============================================
     MOBILE SUMMARY CARD
     ============================================ */
  .mobile-summary-card {
    border-radius: 10px !important;
    border-left: 4px solid #74a533;
    box-shadow: 0 2px 8px rgba(0,0,0,0.08) !important;
    margin-bottom: 16px;
    background: white !important;
  }

  .summary-title {
    font-family: 'Earthwerk', sans-serif;
    font-size: 1rem;
    padding: 10px 16px 6px 16px;
    color: #4a6b22;
  }

  .summary-row {
    display: flex;
    justify-content: space-between;
    padding: 4px 0;
    border-bottom: 1px solid #f0f0f0;
  }

  .summary-row:last-child {
    border-bottom: none;
  }

  .summary-label {
    color: #666;
    font-size: 0.85rem;
  }

  .summary-value {
    font-size: 0.9rem;
    font-weight: 500;
    text-align: right;
  }

  /* ============================================
     CONTENT LAYOUT
     ============================================ */
  .payment-content {
    max-width: 1200px;
    margin: 0 auto;
  }

  .sidebar-col {
    padding-right: 20px;
  }

  .main-content {
    background-color: white;
    border-radius: 10px;
    padding: 20px;
    box-shadow: 0 4px 15px rgba(0,0,0,0.05);
  }

  /* ============================================
     DONATION CARD
     ============================================ */
  .donation-card {
    background-color: #f5f9f0;
    border-radius: 10px;
    padding: 16px;
    margin-bottom: 24px;
    border: 1px solid #e0e8d0;
  }

  .donation-title {
    font-family: 'Earthwerk', sans-serif;
    font-size: 1.2rem;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  }

  .donation-form {
    align-items: center;
  }

  .donation-form .v-container {
    padding: 2px 4px;
  }

  .donate-btn {
    font-weight: bold;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    height: 40px;
    font-size: 0.8rem;
  }

  /* ============================================
     PAYMENT METHODS
     ============================================ */
  .payment-methods {
    margin-bottom: 24px;
  }

  .methods-title {
    font-family: 'Earthwerk', sans-serif;
    font-size: 1.2rem;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
  }

  .method-cards {
    margin: 0 -6px;
    display: flex;
    flex-wrap: wrap;
  }

  .method-item {
    padding: 0 6px;
    margin-bottom: 12px;
    display: flex;
  }

  .method-item .method-card {
    width: 100%;
  }

  .method-card {
    border-radius: 10px !important;
    transition: all 0.3s ease;
    cursor: pointer;
    border: 2px solid transparent;
    height: 100%;
    display: flex;
    flex-direction: column;
    position: relative;
    overflow: hidden;
  }

  .method-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
    border-color: #74a533;
  }

  .active-method {
    border-color: #74a533;
    background-color: #f5f9f0;
  }

  .selected-method {
    border: 2px solid #1976D2 !important;
    box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.2) !important;
  }

  .method-card::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 0 30px 30px 0;
    border-color: transparent #1976D2 transparent transparent;
    transition: all 0.3s ease;
    opacity: 0;
  }

  .selected-method::after {
    opacity: 1;
  }

  .method-icon {
    padding: 16px;
    text-align: center;
    flex-grow: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 80px;
  }

  .method-icon img {
    max-width: 70%;
    max-height: 80px;
    object-fit: contain;
  }

  .method-title {
    padding: 4px 12px !important;
  }

  .method-name {
    font-size: 1rem;
    margin-bottom: 2px;
  }

  .method-description {
    color: #666;
    font-size: 0.75rem;
  }

  .method-selection {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 4px 12px 12px 12px !important;
  }

  .selected-indicator {
    display: flex;
    align-items: center;
    color: #1976D2;
    font-weight: 500;
    font-size: 0.8rem;
    margin-left: 8px;
  }

  .selected-indicator .v-icon {
    margin-right: 4px;
  }

  .active-method {
    background-color: rgba(25, 118, 210, 0.05);
  }

  /* ============================================
     PAYMENT FORMS
     ============================================ */
  .payment-form {
    margin-top: 20px;
  }

  .form-card {
    border-radius: 10px !important;
    overflow: hidden;
  }

  .form-title {
    font-family: 'Earthwerk', sans-serif;
    font-size: 1.1rem;
    padding: 14px 16px;
    background-color: #f5f9f0;
    border-bottom: 1px solid #e0e8d0;
  }

  .payment-actions {
    margin: 16px 0;
  }

  .payment-actions .v-btn {
    font-size: 0.85rem;
    height: 48px !important;
  }

  .payment-security {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #666;
    font-size: 0.8rem;
    margin-bottom: 16px;
    flex-wrap: wrap;
  }

  .payment-security span {
    margin-left: 6px;
  }

  .email-confirmation {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 12px;
    background-color: #fff8e1;
    border-radius: 5px;
    font-size: 0.8rem;
    margin-top: 16px;
    flex-wrap: wrap;
    text-align: center;
  }

  .email-confirmation .v-icon {
    margin-right: 8px;
  }

  /* ============================================
     MPESA SPECIFIC
     ============================================ */
  .mpesa-notice {
    display: flex;
    align-items: center;
    padding: 12px;
    background-color: #fff8e1;
    border-radius: 5px;
    font-size: 0.8rem;
    margin-bottom: 16px;
    flex-wrap: wrap;
  }

  .mpesa-notice .v-icon {
    margin-right: 8px;
  }

  .confirm-payment {
    margin-top: 16px;
  }

  .confirm-payment .v-btn {
    font-size: 0.85rem;
    height: 48px !important;
  }

  .payment-instructions {
    margin-top: 12px;
    padding: 10px;
    background-color: #f5f5f5;
    border-radius: 5px;
    text-align: center;
    font-size: 0.8rem;
    color: #666;
  }

  /* ============================================
     ANIMATIONS
     ============================================ */
  .slide-fade-enter-active {
    transition: all 0.3s ease;
  }
  .slide-fade-leave-active {
    transition: all 0.3s cubic-bezier(1, 0.5, 0.8, 1);
  }
  .slide-fade-enter, .slide-fade-leave-to {
    transform: translateY(10px);
    opacity: 0;
  }

  .fade-enter-active, .fade-leave-active {
    transition: opacity 0.5s;
  }
  .fade-enter, .fade-leave-to {
    opacity: 0;
  }

  /* ============================================
     EARTH COLOR
     ============================================ */
  .earth-color {
    color: #4a6b22;
  }

  /* ============================================
     RESPONSIVE BREAKPOINTS
     ============================================ */

  /* ========== TABLET & SMALL LAPTOP ========== */
  @media (max-width: 1264px) {
    .payment-container {
      padding: 12px;
    }
    .payment-title {
      font-size: 1.8rem;
    }
    .main-content {
      padding: 16px;
    }
  }

  /* ========== TABLET (PORTRAIT) ========== */
  @media (max-width: 960px) {
    .sidebar-col {
      display: none;
    }
    .main-content {
      margin-left: 0 !important;
      width: 100%;
      padding: 16px;
    }
    .payment-title {
      font-size: 1.6rem;
    }
    .progress-step {
      padding: 6px 12px;
      font-size: 0.75rem;
    }
    .progress-step:not(:last-child):after {
      right: -12px;
    }
    .method-item {
      flex: 0 0 50%;
      max-width: 50%;
    }
    .method-icon img {
      max-height: 70px;
    }
    .method-name {
      font-size: 0.9rem;
    }
  }

  /* ========== MOBILE ========== */
  @media (max-width: 600px) {
    .payment-container {
      padding: 8px;
    }
    .payment-header {
      padding: 12px;
      margin-bottom: 12px;
    }
    .payment-title {
      font-size: 1.3rem;
      margin-bottom: 8px;
    }
    .payment-progress {
      gap: 4px;
    }
    .progress-step {
      padding: 4px 10px;
      font-size: 0.65rem;
      border-radius: 12px;
      margin: 0 2px;
    }
    .progress-step:not(:last-child):after {
      content: '';
      display: none;
    }
    .main-content {
      padding: 12px;
      border-radius: 8px;
    }
    .donation-card {
      padding: 12px;
    }
    .donation-title {
      font-size: 1rem;
    }
    .donation-title .v-icon {
      font-size: 18px !important;
    }
    .donation-form .v-container {
      padding: 2px 0;
    }
    .donate-btn {
      font-size: 0.7rem;
      height: 36px !important;
      padding: 0 12px !important;
    }
    .methods-title {
      font-size: 1rem;
    }
    .methods-title .v-icon {
      font-size: 18px !important;
    }
    .method-item {
      flex: 0 0 100%;
      max-width: 100%;
      padding: 0 4px;
      margin-bottom: 8px;
    }
    .method-card {
      flex-direction: row;
      flex-wrap: wrap;
      align-items: center;
    }
    .method-icon {
      flex: 0 0 60px;
      padding: 10px;
      min-height: auto;
    }
    .method-icon img {
      max-height: 40px;
      max-width: 50px;
    }
    .method-title {
      flex: 1;
      padding: 4px 8px !important;
    }
    .method-name {
      font-size: 0.8rem;
    }
    .method-description {
      font-size: 0.65rem;
    }
    .method-selection {
      padding: 4px 8px 8px 8px !important;
      width: 100%;
    }
    .selected-indicator {
      font-size: 0.7rem;
    }
    .form-title {
      font-size: 0.95rem;
      padding: 10px 12px;
    }
    .form-card .v-container {
      padding: 8px !important;
    }
    .payment-actions {
      margin: 12px 0;
    }
    .payment-actions .v-btn {
      font-size: 0.75rem;
      height: 40px !important;
    }
    .payment-security {
      font-size: 0.7rem;
      flex-direction: column;
      text-align: center;
    }
    .payment-security .v-icon {
      font-size: 18px !important;
    }
    .payment-security span {
      margin-left: 0;
      margin-top: 4px;
    }
    .email-confirmation {
      font-size: 0.7rem;
      padding: 10px;
      flex-direction: column;
      text-align: center;
    }
    .email-confirmation .v-icon {
      font-size: 18px !important;
      margin-right: 0;
      margin-bottom: 4px;
    }
    .mpesa-notice {
      font-size: 0.7rem;
      padding: 10px;
      flex-direction: column;
      text-align: center;
    }
    .mpesa-notice .v-icon {
      font-size: 18px !important;
      margin-right: 0;
      margin-bottom: 4px;
    }
    .confirm-payment .v-btn {
      font-size: 0.75rem;
      height: 40px !important;
    }
    .payment-instructions {
      font-size: 0.7rem;
      padding: 8px;
    }
    /* Summary Card Mobile */
    .summary-title {
      font-size: 0.9rem;
      padding: 8px 12px 4px 12px;
    }
    .summary-row {
      padding: 3px 0;
    }
    .summary-label {
      font-size: 0.75rem;
    }
    .summary-value {
      font-size: 0.8rem;
    }
    .mobile-summary-card {
      margin-bottom: 12px;
    }
    .mobile-summary-card .pa-3 {
      padding: 8px 12px !important;
    }
  }

  /* ========== VERY SMALL PHONES ========== */
  @media (max-width: 360px) {
    .payment-container {
      padding: 4px;
    }
    .payment-title {
      font-size: 1.1rem;
    }
    .progress-step {
      padding: 3px 8px;
      font-size: 0.55rem;
    }
    .main-content {
      padding: 8px;
    }
    .donation-title {
      font-size: 0.85rem;
    }
    .methods-title {
      font-size: 0.85rem;
    }
    .method-name {
      font-size: 0.7rem;
    }
    .form-title {
      font-size: 0.8rem;
      padding: 8px 10px;
    }
    .payment-actions .v-btn {
      font-size: 0.65rem;
      height: 36px !important;
    }
    .donate-btn {
      font-size: 0.6rem;
      height: 32px !important;
    }
  }

  /* ========== LANDSCAPE MODE ========== */
  @media (max-height: 500px) and (orientation: landscape) {
    .payment-container {
      padding: 6px;
    }
    .payment-header {
      padding: 8px;
      margin-bottom: 8px;
    }
    .payment-title {
      font-size: 1.2rem;
      margin-bottom: 4px;
    }
    .payment-progress {
      gap: 4px;
      margin-top: 4px;
    }
    .progress-step {
      padding: 3px 10px;
      font-size: 0.6rem;
    }
    .main-content {
      padding: 10px;
    }
    .donation-card {
      padding: 8px;
      margin-bottom: 12px;
    }
    .donation-title {
      font-size: 0.9rem;
      margin-bottom: 4px;
    }
    .method-item {
      flex: 0 0 50%;
      max-width: 50%;
    }
    .method-icon {
      padding: 8px;
      min-height: 50px;
    }
    .method-icon img {
      max-height: 35px;
    }
    .method-name {
      font-size: 0.7rem;
    }
    .payment-actions .v-btn {
      height: 32px !important;
      font-size: 0.65rem;
    }
    .donate-btn {
      height: 28px !important;
      font-size: 0.6rem;
    }
  }

  /* ========== LAPTOP & DESKTOP ========== */
  @media (min-width: 1200px) {
    .payment-container {
      padding: 30px 40px;
    }
    .payment-title {
      font-size: 2.5rem;
    }
    .main-content {
      padding: 30px;
    }
    .method-icon img {
      max-height: 120px;
    }
    .method-name {
      font-size: 1.1rem;
    }
  }

  /* ========== LARGE SCREENS ========== */
  @media (min-width: 1600px) {
    .payment-content {
      max-width: 1400px;
    }
    .payment-title {
      font-size: 3rem;
    }
    .main-content {
      padding: 40px;
    }
  }

  /* ============================================
     FLEX FIXES FOR VUETIFY
     ============================================ */
  .donation-form .v-flex {
    display: flex;
    align-items: center;
  }
  .donation-form .v-container {
    width: 100%;
    padding: 2px 4px;
  }
  .donation-form .v-text-field,
  .donation-form .v-autocomplete {
    width: 100%;
  }

  /* Fix for radio buttons on mobile */
  .v-radio .v-label {
    font-size: 0.85rem;
  }
  @media (max-width: 600px) {
    .v-radio .v-label {
      font-size: 0.75rem;
    }
  }

  /* Fix for dense inputs on mobile */
  .v-text-field--dense .v-label {
    font-size: 0.8rem;
  }
  .v-text-field--dense .v-input__slot {
    min-height: 36px !important;
  }
  @media (max-width: 600px) {
    .v-text-field--dense .v-label {
      font-size: 0.7rem;
    }
    .v-text-field--dense .v-input__slot {
      min-height: 32px !important;
    }
  }

  /* ============================================
     UTILITY - Show/Hide Mobile Summary
     ============================================ */
  @media (min-width: 961px) {
    .hidden-md-and-up {
      display: none !important;
    }
  }
  @media (max-width: 960px) {
    .hidden-md-and-up {
      display: block !important;
    }
  }
</style>
